import client from "@/api/client";
import { GoogleSSOQueryParams } from "@/api/dto/GoogleSSOQueryParams";
import { API_BASE_URL } from "@/api/url";
import { useClient } from "@/composables/useClient";
import { useEnv } from "@/composables/useEnv";
import { useUser } from "@/composables/useUser";
import { defineStore } from "pinia";
import { useNotificationStore } from "./notification";
import type { PersonMember } from "../../cbi-generator/generated/dto/PersonMember";
import { AuthAudience } from "../../cbi-generator/generated/dto/AuthAudience";
import type { AuthTokens } from "../../cbi-generator/generated/dto/AuthTokens";
import type { JwtToken } from "../../cbi-generator/generated/dto/JwtToken";
import type { UserStaff } from "../../cbi-generator/generated/dto/UserStaff";
import type { OrderRef } from "../../cbi-generator/generated/dto/OrderRef";
import type { CompleteMemberProfile } from "../../cbi-generator/generated/dto/CompleteMemberProfile";
import type { MemberProfileUpdate } from "../../cbi-generator/generated/dto/MemberProfileUpdate";
import {
  createSingleFlight,
  decodeAuthToken,
  isRefreshRejected,
  restoreAuthTokens,
} from "@/composables/authSession";
import axios from "axios";

const env = useEnv();

const ESCALATION_DEFAULT_DURATION = 5 * 60; // 5 minutes

async function authenticationService(authenticated = false) {
  const [{ AuthenticationApiClient }, { AuthenticationFrontendService }] =
    await Promise.all([
      import("../../cbi-generator/frontend/api/AuthenticationApiClient"),
      import("../../cbi-generator/frontend/services/AuthenticationFrontendService"),
    ]);
  return new AuthenticationFrontendService(
    new AuthenticationApiClient(authenticated ? useClient() : client),
  );
}

async function memberService(authenticated = false) {
  const [{ MemberApiClient }, { MemberFrontendService }] = await Promise.all([
    import("../../cbi-generator/frontend/api/MemberApiClient"),
    import("../../cbi-generator/frontend/services/MemberFrontendService"),
  ]);
  return new MemberFrontendService(
    new MemberApiClient(authenticated ? useClient() : client),
  );
}
const audience =
  env.mode === "STAFF" ? AuthAudience.STAFF : AuthAudience.MEMBER;
const refreshSingleFlight = createSingleFlight<void>();
const protoAuthSingleFlight = createSingleFlight<AuthTokens>();
let sessionVersion = 0;
let redirectingToLogin = false;

function initialTokens() {
  try {
    return restoreAuthTokens(localStorage.getItem(env.withPrefix("cbi_auth")));
  } catch (error) {
    console.warn("Discarding invalid persisted authentication state", error);
    localStorage.removeItem(env.withPrefix("cbi_auth"));
    return { accessToken: null, refreshToken: null };
  }
}

function isTokenExpired(token: JwtToken) {
  return token.expiration.getTime() <= Date.now();
}

function assertMemberTokens(tokens: AuthTokens, proto: boolean) {
  if (
    tokens.accessToken.audience !== "AUDIENCE_MEMBER" ||
    (tokens.accessToken.claims.isProto === "true") !== proto
  )
    throw new Error(
      proto
        ? "Guest authentication did not return a guest member session."
        : "Authentication did not return a completed member session.",
    );
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    ...initialTokens(),
    escalationToken: null as JwtToken | null,
    currentlyRefreshing: false,
    identityGeneration: 0,
    mergedOrderRef: null as OrderRef | null,
    user: null as UserStaff | null,
    person: null as PersonMember | null,
    loaded: false,
    loading: false,
    escalateCheckInterval: null as NodeJS.Timeout | null,
  }),

  getters: {
    isAuthenticated: (state) => {
      return () => {
        // has unexpired refresh token
        return (
          !!state.refreshToken?.token && !isTokenExpired(state.refreshToken)
        );
      };
    },
    isEscalated: (state) => {
      return !!state.escalationToken;
    },
    isProtoMember: (state) =>
      state.accessToken?.audience === "AUDIENCE_MEMBER" &&
      state.accessToken.claims.isProto === "true",
    isAnonymousProtoMember(): boolean {
      return this.isProtoMember && !this.accessToken?.claims.email;
    },
    isFullMemberSession: (state) =>
      state.accessToken?.audience === "AUDIENCE_MEMBER" &&
      state.accessToken.claims.isProto !== "true" &&
      !!state.refreshToken?.token &&
      !isTokenExpired(state.refreshToken),
    // A guest session with an email is a newly created account whose profile/password come later.
    hasMemberAccount: (state) =>
      state.accessToken?.audience === "AUDIENCE_MEMBER" &&
      (state.accessToken.claims.isProto !== "true" ||
        !!state.accessToken.claims.email) &&
      !!state.refreshToken?.token &&
      !isTokenExpired(state.refreshToken),
    refreshRequired: (state) => {
      return () => {
        if (state.escalationToken && !isTokenExpired(state.escalationToken)) {
          return false;
        }
        // Check if the access token is expired
        if (!state.accessToken) {
          return true;
        }
        return isTokenExpired(state.accessToken);
      };
    },
    refreshTokenExpired: (state) => {
      return () => {
        if (!state.refreshToken) {
          return true;
        }
        // Check if the refresh token is expired
        return isTokenExpired(state.refreshToken);
      };
    },
    escalationTokenExpired: (state) => {
      return () => {
        if (!state.escalationToken) {
          return true;
        }
        // Check if the refresh token is expired
        if (isTokenExpired(state.escalationToken)) {
          state.escalationToken = null;
          return true;
        }
        return false;
      };
    },
    googleSSOUrl(): string {
      return `${API_BASE_URL}/staff/auth/oauth`;
    },
    getAccessToken(): string {
      return this.accessToken?.token || "";
    },
    permissions(state): string[] {
      let perms = state.accessToken?.permissions;

      if (!this.escalationTokenExpired()) {
        perms = {
          ...state.accessToken?.permissions,
          ...state.escalationToken?.permissions,
        };
      }
      if (!perms) {
        return [];
      }
      return Object.keys(perms).map((domain) => `${domain}:${perms[domain]}`);
    },
    hasPermission(): (domain: string, action: string) => boolean {
      return (domain: string, action: string) => {
        return this.permissions.includes(`${domain}:${action}`);
      };
    },
  },
  actions: {
    whenLoggedIn(callback: () => any) {
      let hasCalled = false;
      if (this.isAuthenticated()) {
        callback();
        hasCalled = true;
      }
      watch(
        () => this.isAuthenticated(),
        () => {
          if (this.isAuthenticated()) {
            if (!hasCalled) {
              callback();
              hasCalled = true;
            }
          }
        },
      );
    },
    watchLoggedIn(callback: (isAuthenticated: boolean) => Promise<any> | any) {
      callback(this.isAuthenticated());
      watch(
        () => this.isAuthenticated(),
        () => {
          callback(this.isAuthenticated());
        },
      );
    },
    requireOneOfPermissions(
      oneOfPermissions: { domain: string; action: string }[],
    ) {
      const notification = useNotificationStore();
      const any = oneOfPermissions.find((a) =>
        this.hasPermission(a.domain, a.action),
      );
      if (!any) {
        notification.addSnackbar("Insufficient Permissions", "error");
        throw "Insufficient Permissions";
      }
    },
    requireAllOfPermissions(
      allOfPermissions: { domain: string; action: string }[],
    ) {
      const notification = useNotificationStore();
      const all = allOfPermissions.filter((a) =>
        this.hasPermission(a.domain, a.action),
      );
      if (all.length != allOfPermissions.length) {
        notification.addSnackbar("Insufficient Permissions", "error");
        throw "Insufficient Permissions";
      }
    },
    requireAuth(authType: string[]) {
      const notification = useNotificationStore();
      let valid = false;
      if (authType.includes("staff")) {
        if (this.accessToken?.audience == "AUDIENCE_STAFF") {
          valid = true;
        }
      }
      if (authType.includes("member")) {
        if (this.accessToken?.audience == "AUDIENCE_MEMBER") {
          valid = true;
        }
      }
      if (!valid) {
        notification.addSnackbar(
          "Must be logged in as " + authType.join("or "),
          "error",
        );

        throw "Must be logged in";
      }
    },
    async createAuthorizedPerson(email: string) {
      if (this.isProtoMember) {
        if (this.accessToken?.claims.email)
          throw new Error("This session already has an account email.");
        const generation = this.identityGeneration;
        const result = await (
          await memberService(true)
        ).createMemberFromProtoMember(email);
        if (generation !== this.identityGeneration)
          throw new Error("Session changed while creating the account.");
        await this.updateTokens(result.tokens, result.order);
        return;
      }
      await this.updateTokens(
        await (await memberService()).createMemberPublic(email),
      );
    },
    async login(username: string, password: string) {
      const request = {
        audience,
        identity: username,
        password,
      };
      if (this.isProtoMember && audience === AuthAudience.MEMBER) {
        await this.ensureProtoMember();
        const generation = this.identityGeneration;
        const result = await (
          await authenticationService(true)
        ).loginFromProtoMember(request);
        if (generation !== this.identityGeneration)
          throw new Error("Session changed during login");
        assertMemberTokens(result.tokens, false);
        await this.updateTokens(result.tokens, result.order);
        return;
      }
      await this.updateTokens(
        await (await authenticationService()).loginPublic(request),
      );
    },
    async completeProtoPerson(
      request: CompleteMemberProfile,
      profile?: MemberProfileUpdate,
    ) {
      if (audience !== AuthAudience.MEMBER || !this.isProtoMember)
        throw new Error("Only a guest member session can be completed.");
      const generation = this.identityGeneration;
      const member = await memberService(true);
      const tokens = profile
        ? await member.completeRegistrationMember({
            identity: request,
            profile,
          })
        : await member.completeProfileMember(request);
      if (generation !== this.identityGeneration)
        throw new Error("Session changed during guest checkout.");
      assertMemberTokens(tokens, false);
      await this.updateTokens(tokens);
    },
    async ensureProtoMember(): Promise<AuthTokens> {
      if (audience !== AuthAudience.MEMBER)
        throw new Error(
          "Guest sessions are only available in the member portal.",
        );
      return protoAuthSingleFlight(async () => {
        let guestExpired = false;
        if (this.isAnonymousProtoMember && this.refreshTokenExpired()) {
          this.clearTokens();
          this.clearUser();
          guestExpired = true;
        }
        if (this.refreshToken?.token) {
          const anonymous = this.isAnonymousProtoMember;
          const version = sessionVersion;
          try {
            if (!this.isAuthenticated() || this.refreshRequired())
              await this.refresh();
          } catch (error) {
            if (
              !anonymous ||
              !isRefreshRejected(
                axios.isAxiosError(error) ? error.response?.status : undefined,
              ) ||
              sessionVersion !== version + 1 ||
              this.accessToken ||
              this.refreshToken
            )
              throw error;
            guestExpired = true;
          }
          if (this.accessToken && this.refreshToken) {
            if (this.accessToken.audience !== "AUDIENCE_MEMBER")
              throw new Error("A different audience is already signed in.");
            return {
              accessToken: this.accessToken,
              refreshToken: this.refreshToken,
            };
          }
        }
        if (this.accessToken?.token)
          throw new Error(
            "The saved session cannot be restored; sign in again.",
          );
        const version = sessionVersion;
        const tokens = await (
          await authenticationService()
        ).authAsProtoPublic();
        if (version !== sessionVersion)
          throw new Error("Session changed while starting guest checkout.");
        assertMemberTokens(tokens, true);
        await this.updateTokens(tokens);
        if (guestExpired)
          useNotificationStore().addSnackbar(
            "Your guest session expired. A new cart has been started.",
            "info",
          );
        return tokens;
      });
    },
    async updateTokens(
      tokens: AuthTokens,
      mergedOrder: OrderRef | null = null,
    ) {
      this.identityGeneration++;
      this.mergedOrderRef = mergedOrder;
      this.updateEscalationToken(null);
      this.setState(tokens.refreshToken, tokens.accessToken);
      await this.getUserInfo();
    },
    async refresh() {
      return refreshSingleFlight(async () => {
        const version = sessionVersion;
        const refreshToken = this.refreshToken?.token;
        if (!refreshToken) throw new Error("No refresh session");
        this.currentlyRefreshing = true;
        try {
          const tokens = await (
            await authenticationService()
          ).refreshPublic({
            audience,
            refreshToken,
          });
          if (version !== sessionVersion)
            throw new Error("Session changed during refresh");
          this.setState(tokens.refreshToken, tokens.accessToken);
        } catch (error) {
          if (
            version === sessionVersion &&
            isRefreshRejected(
              axios.isAxiosError(error) ? error.response?.status : undefined,
            )
          ) {
            this.clearTokens();
            this.clearUser();
          }
          throw error;
        } finally {
          this.currentlyRefreshing = false;
        }
      });
    },

    /** Ends a session Compass has rejected and sends protected pages to login. */
    async expireSession() {
      const hadSession = !!this.accessToken || !!this.refreshToken;
      // Clearing bumps sessionVersion, which would abort an in-flight guest login.
      if (hadSession) {
        this.clearTokens();
        this.clearUser();
      }
      if (redirectingToLogin) return;
      const { default: router } = await import("@/router");
      const route = router.currentRoute.value;
      if (route.name === "login" || route.meta.requiresAuth === false) return;
      redirectingToLogin = true;
      try {
        if (hadSession)
          useNotificationStore().addSnackbar(
            "Your session has expired. Please log in again.",
            "error",
          );
        await router.push({
          path: "/login",
          query: { redirect: route.fullPath },
        });
      } finally {
        redirectingToLogin = false;
      }
    },

    async loginWithGoogle(params: GoogleSSOQueryParams) {
      // Make API call to authenticate user and retrieve tokens
      const response = await client.get("/staff/auth/oauth_callback", {
        params,
      });

      if (response.status === 200) {
        const { deserializeCbiDto } =
          await import("../../cbi-generator/frontend/serialization/CbiDateCodec");
        await this.updateTokens(
          deserializeCbiDto<AuthTokens>(
            response.data,
            "generated.dto.AuthTokens",
          ),
        );
      } else {
        throw new Error("Login failed");
      }
    },

    async logout() {
      const refreshToken = this.refreshToken?.token;
      this.clearTokens();
      this.clearUser();
      try {
        if (refreshToken)
          await (
            await authenticationService()
          ).logoutPublic({
            audience,
            refreshToken,
          });
      } catch (error) {
        useNotificationStore().addSnackbar(
          "Signed out locally, but server session revocation failed",
          "error",
        );
        throw error;
      }
    },

    clearTokens() {
      sessionVersion++;
      this.identityGeneration++;
      this.accessToken = null;
      this.refreshToken = null;
      this.mergedOrderRef = null;
      this.updateEscalationToken(null);
    },

    setState(refreshToken: JwtToken, accessToken: JwtToken) {
      sessionVersion++;
      // Backend token DTOs omit registered claims such as sub; decode so state matches a reload.
      this.accessToken = decodeAuthToken(accessToken.token);
      this.refreshToken = decodeAuthToken(refreshToken.token);
    },
    // get user info
    async getUserInfo() {
      const generation = this.identityGeneration;
      const { currentUserRef, currentPersonRef, loadUser, awaitFetch } =
        useUser();
      if (this.loading) {
        await awaitFetch();
        if (generation !== this.identityGeneration) return;
        this.user = currentUserRef.value || null;
        this.person = currentPersonRef.value || null;
        return;
      }

      this.loading = true;
      this.loaded = false;
      try {
        await loadUser(true);
        if (generation !== this.identityGeneration) return;
        this.user = currentUserRef.value || null;
        this.person = currentPersonRef.value || null;
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },

    // clear user info
    clearUser() {
      this.user = null;
      this.person = null;
      this.loaded = false;
    },

    decodeJwt(token: string): JwtToken {
      return decodeAuthToken(token);
    },

    updateEscalationToken(token: JwtToken | null) {
      if (this.escalateCheckInterval) clearInterval(this.escalateCheckInterval);
      this.escalationToken = token;
      this.escalateCheckInterval = token
        ? setInterval(() => {
            if (this.escalationTokenExpired()) {
              this.updateEscalationToken(null);
            }
          }, 1000 * 5)
        : null;
    },

    // escalate to get admin permissions
    async escalate(username: string, password: string) {
      this.updateEscalationToken(
        await (
          await authenticationService(true)
        ).escalatePermissionsStaff({
          granterUsername: username,
          granterPassword: password,
          durationSeconds: ESCALATION_DEFAULT_DURATION,
          permissions: {
            admin: "ADMIN",
          },
        }),
      );
    },
    deescalate() {
      this.updateEscalationToken(null);
    },

    getUserFullName() {
      if (!this.user) return "";
      const { nameFirst, nameLast } = this.user;
      return `${nameFirst || ""} ${nameLast || ""}`.trim();
    },

    async escalateUserSession(password: string) {
      const notification = useNotificationStore();
      try {
        const service = await authenticationService(true);
        this.updateEscalationToken(
          await (audience === AuthAudience.STAFF
            ? service.escalateSessionStaff(password)
            : service.escalateSessionMember(password)),
        );
      } catch (e) {
        notification.addSnackbar("Invalid password", "error");
        throw e;
      }
    },

    async updatePassword(newPassword: string) {
      const notification = useNotificationStore();
      const service = await authenticationService(true);
      const request = { newPassword, logoutSessions: true };
      const tokens = await (audience === AuthAudience.STAFF
        ? service.updatePasswordStaff(request)
        : service.updatePasswordMember(request));
      this.updateEscalationToken(null);
      await this.updateTokens(tokens);
      notification.addSnackbar("Password Updated", "success");
      return true;
    },

    async sendUpdatePasswordEmail(userName: string) {
      const sent = await (
        await authenticationService()
      ).sendPasswordResetPublic({
        audience,
        identity: userName,
      });
      if (!sent) throw new Error("Unable to send password reset email");
      return sent;
    },
  },
});
